import { useEffect, useState, useContext, HTMLAttributes } from 'react';

import { User } from 'firebase/auth';

import DiscussionQuestionsSidebarContent from './DiscussionQuestionsSidebarContent';
import DiscussionThreadContent from './DiscussionThreadContent';
import CommentApi from '../../../../api/commentApi';
import CommentService from '../../../../services/commentService';
import CommentData from '../../../../types/commentData';
import { AuthContext } from '../../../../utils/AuthProvider';

import './Discussion.scss';
import NotificationService from 'services/notificationService';
import { useLocation } from 'react-router-dom';
import { bgFade } from '../../../../utils/functions';

enum State {
  OVERVIEW,
  COMMENT_THREAD,
}

interface DiscussionSidebarContentProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Id of the context object (either a module id or a skill id).
   */
  contextId: string;
  context: 'SKILL' | 'SECTION';
  className?: string;
}

export default function DiscussionSwitchSidebarContent({ contextId, context }: DiscussionSidebarContentProps) {
  // States
  const [comments, setComments] = useState<CommentData[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>();
  const [state, setState] = useState<State>(State.OVERVIEW);

  const { user } = useContext(AuthContext)!;
  const { hash } = useLocation();

  const selectedQuestion = comments.find(comment => comment.id === selectedQuestionId);
  const responseComments = selectedQuestion
    ? comments.filter(comment => comment.topLevelCommentId === selectedQuestion.id)
    : [];

  useEffect(() => {
    async function fetchData() {
      const fetchedComments = await CommentApi.getCommentsByContextId(contextId);
      setComments(fetchedComments);
    }

    fetchData();
  }, [contextId]);

  useEffect(() => {
    const match = hash.match(/comment:(\w+)/);
    const highlightCommentId = match?.[1];
    if (highlightCommentId) {
      bgFade(highlightCommentId);
    }
  }, [hash, comments]);

  async function addComment(
    commentBody: string,
    isAnonymous: boolean,
    topLevelCommentId?: string,
    replyingToId?: string
  ) {
    const newComment = {
      body: commentBody,
      contextId,
      topLevelCommentId: topLevelCommentId ? topLevelCommentId : null,
      replyingToId: replyingToId ? replyingToId : null,
      userId: isAnonymous ? null : user!.uid,
      isDeleted: false,
    };
    const updatedComments = await CommentService.insertCommentInArray(newComment, comments);
    const updatedNewComment = updatedComments[updatedComments.length - 1];

    switch (context) {
      case 'SKILL':
        await NotificationService.createAndSendSkillQuestionNotification(updatedNewComment);
        break;
      case 'SECTION':
        await NotificationService.createAndSendSectionQuestionNotification(updatedNewComment);
    }

    setComments(updatedComments);
  }

  async function deleteComment(commentToDelete: CommentData) {
    const updatedComments = await CommentService.deleteComment(commentToDelete, comments);
    setComments(updatedComments);
  }

  function showPromptIfNotLoggedIn(user: User | null) {
    if (!user) {
      window.prompt('Please sign in to vote!');
      return;
    }
  }

  async function thumbsUpComment(comment: CommentData, user: User | null) {
    showPromptIfNotLoggedIn(user);
    const updatedComments = await CommentService.upvoteCommentInArray(comment, user!.uid, comments);
    setComments(updatedComments);
  }

  async function thumbsDownComment(comment: CommentData) {
    const updatedComments = await CommentService.downvoteCommentInArray(comment, user!.uid, comments);
    setComments(updatedComments);
  }

  function showCommentThread(comment: CommentData) {
    setSelectedQuestionId(comment.id);
    setState(State.COMMENT_THREAD);
  }

  return (
    <>
      {/*      <ol className="breadcrumbs mb-4">
        <li className="breadcrumb">
          <button onClick={() => setState(State.OVERVIEW)}>Discussion</button>
        </li>
        {state === State.COMMENT_THREAD && focusedComment && (
          <li className="breadcrumb">
            <button className="inherit">
              {focusedComment.body.length > 19 ? focusedComment.body.substring(0, 19) + '...' : focusedComment.body}
            </button>
          </li>
        )}
      </ol>*/}
      {state === State.COMMENT_THREAD && selectedQuestion && (
        <DiscussionThreadContent
          question={selectedQuestion}
          onReply={addComment}
          responseComments={responseComments}
          onThumbsUp={thumbsUpComment}
          onThumbsDown={thumbsDownComment}
          onDeleteComment={deleteComment}
        />
      )}
      {state === State.OVERVIEW && (
        <DiscussionQuestionsSidebarContent
          onClickComment={showCommentThread}
          onAddComment={addComment}
          comments={comments}
        />
      )}
    </>
  );
}
